<template>
  <v-dialog
    v-model="dialog"
    :max-width="options.width"
    :style="{ zIndex: options.zIndex }"
    @keydown.esc="cancel"
  >
    <v-card>
      <v-container
        :class="{
          'px-10': !mobile,
          'pt-8': !mobile,
          'px-6': mobile,
          'pt-4': mobile
        }"
      >
        <p class="subtitle-1 font-weight-bold mb-2">Create Payment Request</p>
        <v-row>
          <v-col class="py-0" md="3">
            <p class="caption">Tenant:</p>
          </v-col>
          <v-col class="py-0" md="9">
            <p class="caption font-weight-bold">{{ tenantName }}</p>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="py-0" md="3">
            <p class="caption">Credits:</p>
          </v-col>
          <v-col class="py-0" md="9">
            <p class="caption font-weight-bold">
              {{ formatCurrency(-balance) }}
            </p>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <v-row>
          <v-col class="py-0" :md="!mobile ? 6 : null">
            <v-select
              v-model="category"
              :items="categories"
              label="Select Category"
              dense
              outlined
              @change="changeCategory"
            >
              <template v-slot:append-item>
                <v-list-item>
                  <p class="caption grey--text">
                    Rental payment requests are automatically generated
                  </p>
                </v-list-item>
              </template>
              <template v-slot:prepend-inner>
                <v-icon small class="mt-1">mdi-tools</v-icon>
              </template>
            </v-select>
          </v-col>
          <v-col class="py-0" cols="12" sm="6">
            <v-currency-field
              v-model="amount"
              type="number"
              label="Amount"
              dense
              outlined
            >
              <template v-slot:prepend-inner>
                <v-icon small class="mt-1">mdi-cash</v-icon>
              </template>
            </v-currency-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="py-0" cols="12" sm="12" md="4">
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="startDate"
                  label="Start Date"
                  v-bind="attrs"
                  readonly
                  dense
                  outlined
                  v-on="on"
                >
                  <template v-slot:prepend-inner>
                    <v-icon small class="mt-1">mdi-calendar-month</v-icon>
                  </template>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="startDate"
                no-title
                scrollable
                @input="startDateMenu = false"
              >
              </v-date-picker>
            </v-menu>
          </v-col>
          <v-col class="py-0" cols="12" sm="12" md="4">
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="endDate"
                  label="End Date"
                  v-bind="attrs"
                  readonly
                  dense
                  outlined
                  v-on="on"
                >
                  <template v-slot:prepend-inner>
                    <v-icon small class="mt-1">mdi-calendar-month</v-icon>
                  </template>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="endDate"
                no-title
                scrollable
                @input="endDateMenu = false"
              >
              </v-date-picker>
            </v-menu>
          </v-col>
          <v-col class="py-0" cols="12" sm="12" md="4">
            <v-menu
              v-model="dueDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dueDate"
                  label="Due Date"
                  v-bind="attrs"
                  readonly
                  dense
                  outlined
                  v-on="on"
                >
                  <template v-slot:prepend-inner>
                    <v-icon small class="mt-1">mdi-calendar-month</v-icon>
                  </template>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="dueDate"
                no-title
                scrollable
                @input="dueDateMenu = false"
              >
              </v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
        <v-form v-model="isDescriptionValid">
          <v-row>
            <v-col class="py-0" cols="12" sm="12">
              <v-textarea
                v-model="description"
                name="description"
                label="Description"
                height="100"
                outlined
                counter="500"
                :rules="descriptionRules"
              >
                <template v-slot:prepend-inner>
                  <v-icon small>mdi-information</v-icon>
                </template>
              </v-textarea>
            </v-col>
          </v-row>
        </v-form>
        <v-row>
          <v-col class="py-0" cols="12" sm="12" md="12">
            <v-file-input
              v-model="attachments"
              accept="image/*, application/pdf"
              label="Click here to upload attachments"
              prepend-icon=""
              chips
              small-chips
              multiple
              dense
              outlined
              hide-details
            >
              <template v-slot:prepend-inner>
                <v-icon small class="mt-1">mdi-paperclip</v-icon>
              </template>
            </v-file-input>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pb-2" cols="12" sm="12" md="12">
            <v-checkbox v-model="confirmSendEmail" class="my-0" hide-details>
              <template v-slot:label> Send email to tenant </template>
            </v-checkbox>
          </v-col>
        </v-row>
        <v-card-actions :class="{ 'pt-6': !mobile }">
          <v-spacer></v-spacer>
          <v-btn
            class="font-weight-bold"
            color="primary darken-1"
            :disabled="!category || !isDescriptionValid"
            text
            @click="finish"
            >Finish</v-btn
          >
          <v-btn
            class="font-weight-bold"
            color="grey darken-1"
            text
            @click="cancel"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-container>
    </v-card>
    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-dialog>
</template>

<script>
import { dayjs } from '../../util/date'
import { forbiddenCreatePaymentRequestCategories } from '../../config/payment'

export default {
  data() {
    return {
      category: '',
      categories: [],
      tenantId: -1,
      bedId: -1,
      bedName: '',
      dialog: false,
      options: {
        color: 'primary',
        width: 700,
        zIndex: 200
      },
      startDate: dayjs().startOf('day').format('YYYY-MM-DD'),
      endDate: dayjs().startOf('day').format('YYYY-MM-DD'),
      startDateMenu: false,
      endDateMenu: false,
      description: '',
      amount: 0,
      attachments: null,
      loading: true,
      tenantName: '',
      balance: 0,
      leaseId: -1,
      dueDate: dayjs().startOf('day').format('YYYY-MM-DD'),
      dueDateMenu: false,
      confirmSendEmail: false,
      isDescriptionValid: false,
      descriptionRules: [
        (v) => v.length <= 500 || 'Please enter at most 500 characters'
      ]
    }
  },
  computed: {
    mobile() {
      return this.$vuetify.breakpoint.xs
    }
  },
  methods: {
    async open(tenant) {
      const leaseDetails = (tenant && tenant.lease_details) || tenant
      this.leaseId = tenant.leaseId
      this.loading = true
      this.tenantId = leaseDetails.tenantId
      this.bedId = tenant.bedId
      this.bedName = tenant.bedName
      this.dialog = true
      this.balance = await this.$store.dispatch(
        'units/fetchTenantBalanceAction',
        {
          tenantId: leaseDetails.tenantId
        }
      )
      this.confirmSendEmail = false
      if (tenant.tenantAlias) {
        this.tenantName = tenant.tenantAlias
      } else {
        this.tenantName = ''
        const tenant_details =
          tenant.tenant_details || (leaseDetails && leaseDetails.tenant_details)
        if (tenant_details.firstName)
          this.tenantName += tenant_details.firstName
        if (tenant_details.lastName)
          this.tenantName += ' ' + tenant_details.lastName
        if (
          !tenant_details.firstName &&
          !tenant_details.lastName &&
          tenant_details.username
        )
          this.tenantName = tenant_details.username
      }
      this.attachments = []
      //this.options = Object.assign(this.options, options)

      const categories =
        this.$store.getters['properties/getProperty']
          .payment_categories_details[0].categories

      this.categories = categories.filter(
        (category) =>
          !forbiddenCreatePaymentRequestCategories.includes(category.value)
      )

      this.loading = false
      return new Promise((resolve, reject) => {
        this.resolve = resolve
        this.reject = reject
      })
    },
    async finish() {
      try {
        this.loading = true
        this.sanitizeDescription(this.description)

        await this.$store.dispatch('payments/collectUtilities', {
          tenantId: this.tenantId,
          bedId: this.bedId,
          category: this.category,
          description: this.description,
          amount: this.amount,
          startDate: dayjs(this.startDate).format(),
          endDate: dayjs(this.endDate).format(),
          attachments: this.attachments,
          leaseId: this.leaseId,
          dueDate: dayjs(this.dueDate).format(),
          confirmSendEmail: this.confirmSendEmail
        })
        // if(this.category === 'violation') {
        //   const violation = await this.$store.dispatch('violations/newViolationAction',
        //     {
        //       tenantId: this.tenantId,
        //       description: this.description,
        //       date: this.date,
        //     }
        //   )
        // }
        this.dialog = false
        this.loading = false

        this.$root
          .$confirm({
            title: 'Create Payment Request',
            message: `The payment request has been sent.`,
            reconfirmOnly: true,
            confirmText: 'OK',
            options: {
              color: 'success'
            }
          })
          .then(() => {
            this.resolve(true)
          })
      } catch (err) {
        this.loading = false
        this.$root.$confirm({
          title: 'Create Payment Request',
          message: err,
          options: {
            color: 'error'
          },
          reconfirmOnly: true,
          confirmText: 'OK'
        })
      }
    },
    cancel() {
      this.dialog = false
      this.loading = false
    },
    formatCurrency(value) {
      if (value == null) return ''
      return parseFloat(value / 100).toLocaleString('en-PH', {
        style: 'currency',
        currency: 'PHP'
      })
    },
    changeCategory() {
      const category = this.categories.find((cat) => cat.value == this.category)
      this.description = `${category.text} bill for ${this.bedName}`
    },
    sanitizeDescription() {
      const newLineAtEndOfStringRegex = /\n$/
      while (newLineAtEndOfStringRegex.test(this.description)) {
        this.description = this.description.replace(
          newLineAtEndOfStringRegex,
          ''
        )
      }
    }
  }
}
</script>

<style scoped>
p {
  margin-bottom: 0;
}
</style>
